import React from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../context/useAuth'
import { APP_VERSION } from '../version'
import Badge from './ui/Badge'
import Footer from './Footer'
import ChatBot from './ChatBot'
import CreditsBadge from './CreditsBadge'
import { BrandMark } from './StudioAura'

const NAV_LABELS = {
  'nav.dashboard': 'Dashboard',
  'nav.upload': 'Upload',
  'nav.buyCredits': 'Credits bestellen',
  'nav.jobs': 'Jobs',
  'nav.masters': 'Meine Masters',
  'nav.compare': 'Vergleich',
  'nav.settings': 'Einstellungen',
  'nav.track': 'Track',
  'page.dashboard': 'Dashboard',
  'page.masters': 'Meine Masters',
  'page.compare': 'Vergleich',
  'page.settings': 'Einstellungen',
}

const navItems = [
  { to: '/dashboard', end: true, labelKey: 'nav.dashboard', icon: '◉' },
  { to: '/dashboard/upload', end: false, labelKey: 'nav.upload', icon: '↑' },
  { to: '/dashboard/buy-credits', end: false, labelKey: 'nav.buyCredits', icon: '◇' },
  { to: '/dashboard/jobs', end: false, labelKey: 'nav.jobs', icon: '◷' },
  { to: '/dashboard/history', end: false, labelKey: 'nav.masters', icon: '♪' },
  { to: '/dashboard/compare', end: false, labelKey: 'nav.compare', icon: '⇄' },
  { to: '/dashboard/settings', end: false, labelKey: 'nav.settings', icon: '⚙' },
]

function getPageTitle(pathname) {
  if (pathname === '/dashboard' || pathname === '/dashboard/') return NAV_LABELS['page.dashboard']
  if (pathname === '/dashboard/upload') return NAV_LABELS['nav.upload']
  if (pathname === '/dashboard/buy-credits') return NAV_LABELS['nav.buyCredits']
  if (pathname === '/dashboard/jobs') return NAV_LABELS['nav.jobs']
  if (pathname === '/dashboard/history') return NAV_LABELS['page.masters']
  if (pathname === '/dashboard/compare') return NAV_LABELS['page.compare']
  if (pathname === '/dashboard/settings') return NAV_LABELS['page.settings']
  if (pathname === '/dashboard/faq') return 'FAQ'
  if (pathname === '/dashboard/impressum') return 'Impressum'
  if (pathname === '/dashboard/datenschutz') return 'Datenschutz'
  if (pathname === '/dashboard/agb') return 'AGB'
  if (pathname === '/dashboard/nutzungsbedingungen') return 'Nutzungsbedingungen'
  if (pathname === '/dashboard/anleitung') return 'Anleitung'
  if (pathname === '/dashboard/admin') return 'Admin'
  if (pathname.startsWith('/dashboard/tracks/')) return NAV_LABELS['nav.track']
  return ''
}

export default function Layout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const pageTitle = getPageTitle(location.pathname)

  return (
    <div className="flex min-h-screen bg-studio-bg">
      {/* Sidebar: nur ab md */}
      <aside className="fixed left-0 top-0 z-40 hidden h-full w-[260px] flex-col border-r border-white/10 bg-black/50 backdrop-blur-xl md:flex">
        <div className="flex h-16 items-center gap-3 border-b border-white/10 px-5">
          <div className="layout-logo flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#00d4aa]/25 bg-[#00d4aa]/10 text-[#00d4aa]">
            <BrandMark size={22} />
          </div>
          <span className="truncate text-sm font-semibold tracking-tight text-white">
            AI Mastering <span className="font-normal text-white/45">Studio</span>
          </span>
        </div>
        <nav className="flex-1 space-y-0.5 p-3" aria-label="Hauptnavigation">
          {navItems.map(({ to, end, labelKey, icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive ? 'active' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <span className="text-lg opacity-90" aria-hidden>{icon}</span>
              <span>{NAV_LABELS[labelKey] ?? labelKey}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin"
              end={false}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive ? 'active' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <span className="text-lg opacity-90" aria-hidden>⚙</span>
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="rounded-xl bg-white/5 px-3 py-2.5">
            <p className="truncate text-sm text-white/90" title={user?.email}>{user?.email}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {user?.is_admin && <Badge variant="ready">Admin</Badge>}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 py-2.5 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            Abmelden
          </button>
          <p className="mt-2 text-center text-[10px] text-white/40">Build {APP_VERSION}</p>
        </div>
      </aside>

      <div className="flex min-h-screen flex-1 flex-col md:ml-[260px] pb-16 md:pb-0">
        <header className="sticky top-0 z-30 flex h-14 min-h-[48px] shrink-0 items-center justify-between border-b border-white/10 bg-studio-bg/90 px-4 backdrop-blur-xl sm:px-6">
          <span className="truncate text-sm font-medium text-white/70">{pageTitle}</span>
          <span className="hidden truncate text-sm text-white/50 max-w-[200px] sm:inline">{user?.email}</span>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>

        <Footer />

        {/* Bottom-Navigation: nur auf kleinen Bildschirmen */}
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-white/10 bg-studio-bg/95 backdrop-blur-xl md:hidden safe-area-padding"
          aria-label="Navigation"
        >
          {navItems.map(({ to, end, labelKey, icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex min-h-[48px] min-w-[48px] flex-col items-center justify-center gap-0.5 px-2 py-2 text-[10px] transition ${
                  isActive ? 'text-[#00d4aa]' : 'text-white/60 active:bg-white/10'
                }`
              }
            >
              <span className="text-lg" aria-hidden>{icon}</span>
              <span className="truncate max-w-[64px]">{NAV_LABELS[labelKey] ?? labelKey}</span>
            </NavLink>
          ))}
        </nav>

        <ChatBot />
        <CreditsBadge />
      </div>
    </div>
  )
}
